/**
 * Phase 7a — Widget de gestion des relances IA pour un devis ou une facture.
 *
 * Props:
 * - targetType: "quote" | "invoice"
 * - targetId: string
 * - showWhenDisabled (default true): affiche le widget même si feature globale off (avec badge)
 * - featureGloballyDisabled: si true → info banner "activez dans Paramètres" (invoice)
 * - reloadKey: number — bump pour forcer un refetch après changement externe
 *
 * Affichage :
 *  - Toggle activé/désactivé (par target)
 *  - Timeline verticale des 3 relances : icône selon status + date + preview 220 chars
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import {
  Bell, XCircle, Clock, AlertTriangle, MailCheck, PenLine,
} from "lucide-react";

const STATUS_META = {
  scheduled: { Icon: Clock, color: "#94a3b8", label: "Planifiée" },
  sent: { Icon: MailCheck, color: "#5A6338", label: "Envoyée" },
  cancelled: { Icon: XCircle, color: "#64748b", label: "Annulée" },
  failed: { Icon: AlertTriangle, color: "#dc2626", label: "Échec" },
  locked: { Icon: Clock, color: "#94a3b8", label: "En cours…" },
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-BE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

const CANCEL_REASON_LABELS = {
  quote_signed: "Devis signé",
  quote_refused: "Devis refusé",
  quote_expired: "Devis expiré",
  invoice_paid: "Facture payée",
  toggle_disabled_per_target: "Désactivée sur cet élément",
  feature_disabled_global: "Fonction désactivée globalement",
  feature_disabled_at_creation: "Feature OFF à l'émission",
  credit_note_emitted: "Note de crédit émise",
  no_recipient_email: "Pas d'email client",
  target_not_found: "Cible introuvable",
};

export default function RemindersWidget({
  targetType, targetId, featureGloballyDisabled = false, reloadKey = 0,
  hideIfEmpty = false,
}) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/${targetType}s/${targetId}/reminders`);
      setData(data);
    } catch (e) {
      // silencieux : le devis/facture n'a peut-être pas encore de relances
      setData({ reminders: [], disabled_per_target: false });
    }
  };
  useEffect(() => { load(); }, [targetType, targetId, reloadKey]);

  if (!data) return null;
  if (hideIfEmpty && data.reminders.length === 0 && !featureGloballyDisabled) return null;

  const toggle = async (disabled) => {
    setBusy(true);
    try {
      const { data: res } = await api.patch(`/${targetType}s/${targetId}/reminders`, { disabled });
      if (disabled) toast.success(`Relances désactivées (${res.cancelled_scheduled} annulées)`);
      else toast.success(res.rescheduled > 0
        ? `Relances réactivées (${res.rescheduled} planifiées)`
        : "Relances réactivées");
      if (res.note) toast.info(res.note);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/30 p-3" data-testid={`reminders-widget-${targetType}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Bell className="h-3.5 w-3.5 text-[#C4553B]" />
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-300">
            Relances IA
          </span>
          {featureGloballyDisabled && (
            <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] uppercase text-amber-300"
                  data-testid="reminders-feature-off-badge">
              Fonction OFF
            </span>
          )}
        </div>
        <Switch
          checked={!data.disabled_per_target}
          onCheckedChange={(v) => toggle(!v)}
          disabled={busy}
          data-testid={`reminders-toggle-${targetType}`}
        />
      </div>

      {featureGloballyDisabled && (
        <div className="mb-2 rounded border border-amber-800/50 bg-amber-950/10 px-2 py-1.5 text-xs text-amber-300">
          Fonction globale des relances factures désactivée. Activez-la dans
          Paramètres · Facturation.
        </div>
      )}

      {data.reminders.length === 0 ? (
        <div className="text-xs text-slate-500 italic">
          Aucune relance {featureGloballyDisabled ? "planifiable" : "planifiée"}.
        </div>
      ) : (
        <ol className="space-y-1.5">
          {data.reminders.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.scheduled;
            return (
              <li key={r.id} className="flex items-start gap-2 text-xs"
                  data-testid={`reminder-item-${targetType}-${r.sequence_index}`}>
                <meta.Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: meta.color }} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-slate-200">Relance #{r.sequence_index}</span>
                    <span className="rounded px-1.5 py-0.5 text-[10px] uppercase" style={{ background: `${meta.color}22`, color: meta.color }}>
                      {meta.label}
                    </span>
                    {r.generated_by === "fallback_static" && (
                      <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] uppercase text-amber-300">fallback</span>
                    )}
                  </div>
                  <div className="mt-0.5 text-slate-400">
                    {r.status === "sent" && r.sent_at
                      ? `Envoyée le ${fmtDateTime(r.sent_at)}`
                      : r.status === "cancelled" && r.cancelled_at
                      ? `Annulée le ${fmtDateTime(r.cancelled_at)} — ${CANCEL_REASON_LABELS[r.cancelled_reason] || r.cancelled_reason}`
                      : r.status === "scheduled"
                      ? `Prévue le ${fmtDateTime(r.scheduled_at)}`
                      : r.status === "failed"
                      ? `Échec : ${r.error?.slice(0, 80) || "erreur"}`
                      : `Programmée : ${fmtDateTime(r.scheduled_at)}`}
                  </div>
                  {r.status === "sent" && r.body_preview && (
                    <details className="mt-1 text-slate-500">
                      <summary className="cursor-pointer text-slate-400 hover:text-slate-200">
                        <PenLine className="mr-1 inline h-3 w-3" /> Voir le message envoyé
                      </summary>
                      <div className="mt-1 rounded bg-slate-950/40 p-2 whitespace-pre-wrap text-slate-300">
                        <div className="text-slate-500 mb-1">Objet : <span className="text-slate-300">{r.subject_snapshot}</span></div>
                        {r.body_preview}…
                      </div>
                    </details>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
